/** OSS 切图根路径（形象进度总览） */
const OSS_ROOT = 'https://awsacloud.oss-cn-shanghai.aliyuncs.com/AY230050Z/imageFile/ZY250090'

/** 拼接 OSS 图片地址，各段自动 encode */
export function buildOssUrl (...segments) {
  return OSS_ROOT + '/' + segments.map((s) => encodeURIComponent(s)).join('/')
}

/** 跨梁展示状态（与 UI 图例、切图文件名对应） */
export const GIRDER_VISUAL_STATUS = {
  completed: 'completed',
  inProduction: 'inProduction',
  delayedProduction: 'delayedProduction',
  pendingProduction: 'pendingProduction',
  delayedPendingProduction: 'delayedPendingProduction',
  unmatched: 'unmatched'
}

/** 墩柱/盖梁展示状态 */
export const PIER_CAP_VISUAL_STATUS = {
  cast: 'cast',
  unCast: 'unCast',
  unmatched: 'unmatched'
}

/** 总览区跨梁图例 */
export const GIRDER_STATUS_LEGEND = [
  { key: GIRDER_VISUAL_STATUS.completed, label: '已完成', color: '#5CE55C' },
  { key: GIRDER_VISUAL_STATUS.inProduction, label: '生产中', color: '#E5C25C' },
  { key: GIRDER_VISUAL_STATUS.delayedProduction, label: '滞后生产', color: '#D0AB40' },
  { key: GIRDER_VISUAL_STATUS.pendingProduction, label: '待生产', color: '#E55C5C' },
  { key: GIRDER_VISUAL_STATUS.delayedPendingProduction, label: '滞后待生产', color: '#C73333' },
  { key: GIRDER_VISUAL_STATUS.unmatched, label: '未匹配', color: '#A7B2C3' }
]

/** 墩柱/盖梁图例 */
export const PIER_CAP_STATUS_LEGEND = [
  { key: GIRDER_VISUAL_STATUS.completed, label: '已完成/已浇筑', color: '#5CE55C' },
  { key: GIRDER_VISUAL_STATUS.inProduction, label: '生产中', color: '#E5C25C' },
  { key: GIRDER_VISUAL_STATUS.delayedProduction, label: '滞后生产', color: '#D0AB40' },
  { key: GIRDER_VISUAL_STATUS.pendingProduction, label: '待生产/未浇筑', color: '#E55C5C' },
  { key: GIRDER_VISUAL_STATUS.delayedPendingProduction, label: '滞后待生产', color: '#C73333' },
  { key: GIRDER_VISUAL_STATUS.unmatched, label: '未匹配', color: '#A7B2C3' }
]

/** 跨梁展示状态对应颜色（与图例一致） */
export function getGirderStatusColor (status) {
  const item = GIRDER_STATUS_LEGEND.find((legend) => legend.key === status)
  return item ? item.color : '#A7B2C3'
}

/** 跨梁合并展示时的状态优先级（值越大优先级越高） */
const GIRDER_STATUS_PRIORITY = {
  [GIRDER_VISUAL_STATUS.delayedPendingProduction]: 5,
  [GIRDER_VISUAL_STATUS.pendingProduction]: 4,
  [GIRDER_VISUAL_STATUS.delayedProduction]: 3,
  [GIRDER_VISUAL_STATUS.inProduction]: 2,
  [GIRDER_VISUAL_STATUS.completed]: 1,
  [GIRDER_VISUAL_STATUS.unmatched]: 0
}

/** 总览跨梁切图文件名前缀 */
const GIRDER_IMG_PREFIX = {
  [GIRDER_VISUAL_STATUS.completed]: '已完成备份',
  [GIRDER_VISUAL_STATUS.inProduction]: '生产中备份',
  [GIRDER_VISUAL_STATUS.delayedProduction]: '滞后生产中备份',
  [GIRDER_VISUAL_STATUS.pendingProduction]: '待生产中备份',
  [GIRDER_VISUAL_STATUS.delayedPendingProduction]: '滞后待生产中备份',
  [GIRDER_VISUAL_STATUS.unmatched]: '未匹配备份'
}

/** 单梁弹窗/分段详情切图文件名前缀 */
const SINGLE_GIRDER_IMG_PREFIX = {
  [GIRDER_VISUAL_STATUS.completed]: '单梁-已完成备份',
  [GIRDER_VISUAL_STATUS.inProduction]: '单梁-生产中备份',
  [GIRDER_VISUAL_STATUS.delayedProduction]: '单梁-滞后生产中备份',
  [GIRDER_VISUAL_STATUS.pendingProduction]: '单梁-待生产中备份',
  [GIRDER_VISUAL_STATUS.delayedPendingProduction]: '单梁-滞后待生产中备份',
  [GIRDER_VISUAL_STATUS.unmatched]: '单梁-未匹配备份'
}

/**
 * 解析跨梁展示状态（总览 / 分段详情 v2 通用）
 * status: 1-已完成 2-生产中 3-待生产
 * delayStatus: 1-滞后生产 2-滞后待生产
 */
export function resolveOverviewGirderStatus (beam) {
  if (!beam || !beam.id) {
    return GIRDER_VISUAL_STATUS.unmatched
  }
  if (beam.status === 1) {
    return GIRDER_VISUAL_STATUS.completed
  }
  if (beam.status === 2) {
    return beam.delayStatus === 1 ? GIRDER_VISUAL_STATUS.delayedProduction : GIRDER_VISUAL_STATUS.inProduction
  }
  if (beam.status === 3) {
    return beam.delayStatus === 2 ? GIRDER_VISUAL_STATUS.delayedPendingProduction : GIRDER_VISUAL_STATUS.pendingProduction
  }
  return GIRDER_VISUAL_STATUS.unmatched
}

/** 墩柱/盖梁合并状态：未浇筑 > 已浇筑（castStatus: 1-已浇筑 2-未浇筑） */
export function resolvePierCapStatus (list) {
  if (!list || !list.length) {
    return PIER_CAP_VISUAL_STATUS.unmatched
  }
  const hasUnCast = list.some((item) => item.castStatus === 2)
  return hasUnCast ? PIER_CAP_VISUAL_STATUS.unCast : PIER_CAP_VISUAL_STATUS.cast
}

/** 合并多片跨梁状态（取优先级最高） */
export function mergeGirderStatus (beams) {
  return beams.reduce((best, beam) => {
    const current = resolveOverviewGirderStatus(beam)
    return GIRDER_STATUS_PRIORITY[current] > GIRDER_STATUS_PRIORITY[best] ? current : best
  }, GIRDER_VISUAL_STATUS.completed)
}

/** 梁号正序排序 */
export function sortByBeamCode (list) {
  return [...list].sort((a, b) => String(a.beamCode || '').localeCompare(String(b.beamCode || ''), 'zh-CN'))
}

/**
 * 总览跨梁展示列表：未匹配默认 6 片；超过 6 片展示 3+1 合并
 * @returns {Array<{type:'single'|'merged', status, beam?, beams?, count?, index?}>}
 */
export function buildOverviewGirderDisplay (girderList) {
  const sorted = sortByBeamCode(girderList || [])
  if (!sorted.length) {
    return Array.from({ length: 6 }, (_, index) => ({
      type: 'single',
      status: GIRDER_VISUAL_STATUS.unmatched,
      beam: null,
      index
    }))
  }
  if (sorted.length <= 6) {
    return sorted.map((beam, index) => ({
      type: 'single',
      status: resolveOverviewGirderStatus(beam),
      beam,
      beams: index === sorted.length - 1 ? sorted : [],
      index
    }))
  }
  const firstThree = sorted.slice(0, 3).map((beam, index) => ({
    type: 'single',
    status: resolveOverviewGirderStatus(beam),
    beam,
    index
  }))
  firstThree.push({
    type: 'merged',
    status: mergeGirderStatus(sorted),
    beams: sorted,
    count: sorted.length
  })
  return firstThree
}

/** 总览合并跨梁切图（超过 6 片时的汇总展示） */
const MERGED_GIRDER_IMG_NAME = {
  [GIRDER_VISUAL_STATUS.completed]: '汇总已完成@3x.png',
  [GIRDER_VISUAL_STATUS.inProduction]: '汇总生产中@3x.png',
  [GIRDER_VISUAL_STATUS.delayedProduction]: '汇总滞后生产@3x.png',
  [GIRDER_VISUAL_STATUS.pendingProduction]: '汇总待生产@3x.png',
  [GIRDER_VISUAL_STATUS.delayedPendingProduction]: '汇总滞后待生产@3x.png',
  [GIRDER_VISUAL_STATUS.unmatched]: '未匹配备份@3x.png'
}

/** 获取跨梁切图 URL，index 0~5 对应叠放位置 */
export function getGirderImageUrl (status, index = 0, single = false) {
  const folder = single ? '单梁' : '总览'
  const prefixMap = single ? SINGLE_GIRDER_IMG_PREFIX : GIRDER_IMG_PREFIX
  const prefix = prefixMap[status] || prefixMap[GIRDER_VISUAL_STATUS.unmatched]
  const suffix = index === 0 ? '@3x.png' : ` ${index + 1}@3x.png`
  return buildOssUrl('形象进度切图', folder, `${prefix}${suffix}`)
}

/** 合并跨梁切图（按合并后的最高优先级状态） */
export function getGirderMergedImageUrl (status) {
  const fileName = MERGED_GIRDER_IMG_NAME[status] || MERGED_GIRDER_IMG_NAME[GIRDER_VISUAL_STATUS.unmatched]
  return buildOssUrl('形象进度切图', '总览', fileName)
}

/** 进度总览统计卡片图标 */
export function getOverviewStatIconUrl (key) {
  const map = {
    total: '预制梁总数.png',
    completed: '已完成.png',
    inProduction: '生产中.png',
    pendingProduction: '待生产.png'
  }
  return buildOssUrl('形象进度切图', map[key] || map.total)
}

/** 墩柱切图（总览整桥示意图） */
export function getPierImageUrl (status) {
  const map = {
    [PIER_CAP_VISUAL_STATUS.cast]: '墩柱已浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unCast]: '墩柱未浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unmatched]: '墩柱未匹配@3x.png'
  }
  return buildOssUrl('形象进度切图', map[status] || map[PIER_CAP_VISUAL_STATUS.unmatched])
}

/** 盖梁切图（总览整桥示意图） */
export function getCapImageUrl (status) {
  const map = {
    [PIER_CAP_VISUAL_STATUS.cast]: '盖梁已浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unCast]: '盖梁未浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unmatched]: '盖梁未匹配@3x.png'
  }
  return buildOssUrl('形象进度切图', map[status] || map[PIER_CAP_VISUAL_STATUS.unmatched])
}

/** 墩柱切图（分段详情专用，带「详情-」前缀） */
export function getDetailPierImageUrl (status) {
  const map = {
    [PIER_CAP_VISUAL_STATUS.cast]: '详情-墩柱已浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unCast]: '详情-墩柱未浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unmatched]: '详情-墩柱未匹配@3x.png'
  }
  return buildOssUrl('形象进度切图', map[status] || map[PIER_CAP_VISUAL_STATUS.unmatched])
}

/** 盖梁切图（分段详情专用，带「详情-」前缀） */
export function getDetailCapImageUrl (status) {
  const map = {
    [PIER_CAP_VISUAL_STATUS.cast]: '详情-盖梁已浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unCast]: '详情-盖梁未浇筑@3x.png',
    [PIER_CAP_VISUAL_STATUS.unmatched]: '详情-盖梁未匹配@3x.png'
  }
  return buildOssUrl('形象进度切图', map[status] || map[PIER_CAP_VISUAL_STATUS.unmatched])
}

/** 每 10 片梁拆成一组，用于分段详情多行展示 */
export function chunkBeamList (list, pageSize = 10) {
  const rows = []
  for (let i = 0; i < list.length; i += pageSize) {
    rows.push(list.slice(i, i + pageSize))
  }
  return rows
}

/** 格式化实际生产日期展示 */
export function formatProductionDate (start, end) {
  if (!start && !end) {
    return '-'
  }
  if (start && end) {
    return `${start} ~ ${end}`
  }
  return start || end || '-'
}
